import { SkillsActions } from '@/skills/store/slice'
import { ResponseFeedbackActions } from '@/response-feedback/store/slice'
import { useAppDispatch } from '@/store/store-hooks'
import { ReactNode, useContext, useEffect } from 'react'
import { AuthContext } from '@/context/auth-context'
import { useRefreshData } from '@/hooks/use-refresh-data'

/**
 * AppInit Post-Authentication Component
 * - Component to handle application initialization tasks
 * - Happens after authentication
 *
 * BLOCKING:
 * - Can be used to block the application until initialization is complete
 * - Conditionally render children based on initialization status
 *
 * NON-BLOCKING:
 * - Can be used to execute startup functions in the background that are not-blocking
 */
export const AppInitPostAuth = ({ children }: { children: ReactNode }) => {
  const dispatch = useAppDispatch()
  const { refreshTimelyData } = useRefreshData()

  // Auth Context
  const authContext = useContext(AuthContext)
  const user = authContext?.user
  const userIsAnon = user?.isAnonymous
  const { pollingStarted } = authContext

  // Load initial data when the user is authenticated
  if (user && !userIsAnon) {
    dispatch(SkillsActions.fetchSkillsContent())
    dispatch(ResponseFeedbackActions.fetchFeedbackReasons())
  }

  useEffect(() => {
    // When authenticated, refresh timely data that is needed for the application that could change frequently
    if (user && !userIsAnon) {
      // Only force refresh timely data when not polling on checkout
      const forceRefresh = !pollingStarted
      refreshTimelyData(forceRefresh)
    }
  }, [user, userIsAnon, refreshTimelyData, pollingStarted])

  // // EXAMPLE IF BLOCKING EVER NEEDED
  // // Blocking loader UX
  // if (loading) {
  //   return <FullPageLoader />
  // }

  return children
}
